import { config } from '../config';

/**
 * DD-1 (design §5.5, §15) — pointer-gesture arbitration, as a pure classifier.
 *
 * The *timers and listeners* belong to the view (`ui/capture/usePointerGesture.ts`); the
 * *rules* belong here, where a Node suite can drive every boundary. That split matters more than
 * usual on this surface: the rules are four numeric thresholds whose interaction is the whole of
 * DD-1, and a browser pass can demonstrate that a tap works but cannot economically prove that a
 * 43 px drag does **not** swipe.
 *
 * ── The map, as DD-1 resolved it ─────────────────────────────────────────────
 *
 * | Surface | tap | long-press | swipe-left | horizontal pan |
 * |---|---|---|---|---|
 * | Discovery row | increments (DISC-3) | voice note (AUD-2, U-11) | **not offered** | belongs to the pager |
 * | Locked tile (U-9) | increments | voice note | disproves (LIVE-5) | — |
 *
 * > *"Swipe-left-to-disprove exists only on the Locked board. DD-1 explicitly permits this
 * > restriction. The justification is structural: in Discovery the horizontal axis belongs to the
 * > pager, and overloading it **is** the misfire generator."*
 *
 * `swipeLeft` is therefore an **option, defaulted off**, not a behavior every caller inherits. The
 * negative that matters for U-8 is checkable because of that: a horizontal drag on a Discovery row
 * classifies as `none` — it neither disproves nor increments — and the test feeds the identical
 * travel to both option sets to show the difference is the option and nothing else.
 *
 * ── Why `longPressFired` is an input rather than a caller's `if` ─────────────
 *
 * §5.5: *"long-press = 450 ms timer, cancelled by > 10 px movement, **sets `suppressClick` so
 * `pointerup` does not also increment**"*. Suppression is a classification rule — the same
 * pointerup means different things depending on whether the hold already fired — so leaving it to
 * each call site is how one surface eventually records a voice note *and* an increment from one
 * press. It suppresses **every** outcome, including a swipe: once a recording has started, a
 * gesture that also disproved the row would be two captures from one contact.
 */

/** What one completed pointer contact meant. `none` is a real answer: discard, mutate nothing. */
export type GestureKind = 'tap' | 'longPress' | 'swipeLeft' | 'none';

/** How far and how long the contact travelled, in CSS pixels and milliseconds. */
export interface PointerTravel {
  readonly dx: number;
  readonly dy: number;
  readonly elapsedMs: number;
}

export interface GestureOptions {
  /**
   * LIVE-5's swipe-left-to-disprove. **Locked board only** (DD-1). Discovery leaves it `false`, so
   * its horizontal axis is the pager's alone.
   */
  readonly swipeLeft: boolean;
  /** Whether the 450 ms hold already fired for this contact. See the module note. */
  readonly longPressFired: boolean;
}

/**
 * §5.5 — has the contact moved far enough to stop being a press?
 *
 * Used for two things that must agree: cancelling the armed long-press timer while the finger is
 * down, and refusing to call the completed contact a tap. One predicate, so they cannot drift.
 *
 * A non-finite coordinate (a synthetic or malformed event) counts as movement: the safe answer for
 * "should this still be treated as a stationary press?" is no.
 */
export function movedBeyondSlop(travel: PointerTravel): boolean {
  if (!Number.isFinite(travel.dx) || !Number.isFinite(travel.dy)) return true;
  return Math.abs(travel.dx) >= config.TAP_SLOP_PX || Math.abs(travel.dy) >= config.TAP_SLOP_PX;
}

/**
 * §5.5 — `Δx ≤ −SWIPE_MIN_PX ∧ |Δx| > 2|Δy|`.
 *
 * The 2:1 ratio is what keeps a diagonal thumb-drag off the disprove path; the sign is what keeps a
 * rightward drag off it entirely.
 */
export function isSwipeLeft(travel: PointerTravel): boolean {
  if (!Number.isFinite(travel.dx) || !Number.isFinite(travel.dy)) return false;
  return travel.dx <= -config.SWIPE_MIN_PX && Math.abs(travel.dx) > 2 * Math.abs(travel.dy);
}

/**
 * Classifies a completed contact (`pointerup`). `pointercancel` never reaches here — §5.5 says
 * *"`pointercancel` → discard, no mutation"*, and the view drops the contact without classifying it.
 *
 * Order is deliberate and is the behavior, not an implementation detail:
 *
 * 1. **`longPressFired` suppresses everything.** One contact, one capture.
 * 2. **swipe before tap**, and only where the option is on. A 60 px leftward drag over 200 ms
 *    satisfies neither `movedBeyondSlop`-free nor the tap rule, so the ordering only decides
 *    between `swipeLeft` and `none` — but stating it removes the question.
 * 3. **tap** = `|Δ| < TAP_SLOP_PX ∧ Δt < LONG_PRESS_MS`. The upper time bound is not redundant with
 *    `longPressFired`: a hold whose timer was cancelled by movement that later returned inside the
 *    slop would otherwise read as a very slow tap.
 */
export function classifyPointerEnd(travel: PointerTravel, options: GestureOptions): GestureKind {
  if (options.longPressFired) return 'none';
  if (options.swipeLeft && isSwipeLeft(travel)) return 'swipeLeft';
  if (!movedBeyondSlop(travel) && Number.isFinite(travel.elapsedMs)) {
    return travel.elapsedMs < config.LONG_PRESS_MS ? 'tap' : 'none';
  }
  return 'none';
}

/**
 * The Discovery row's options (DISC-3): tap and long-press, and **no swipe**.
 *
 * Exported as a value rather than left to each row to spell, so "Discovery has no row swipe" is one
 * fact in one place that a test can assert directly.
 */
export function discoveryGestureOptions(longPressFired: boolean): GestureOptions {
  return { swipeLeft: false, longPressFired };
}
